import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useIntl } from "react-intl";
import {
  ListRequestOfAgreementFilter,
  AgreementFilter,
  AgreementResponse,
} from "services/KnowfullyAdminClient";
import useGlobalContext from "hooks/globalData";
import AsyncAutocompleteWithQuery, {
  AsyncAutocompleteWithQueryProps,
} from "components/AsyncAutocompleteWithQuery";
import contextStore from "stores/contextStore";

type SelectAgreementProps = {
  value?: string | null;
  filter?: { agreementStatusIds: string[] };
  onChange: (agreementId: string | null) => void;
  parentOnChange?: (value: AgreementResponse | null) => void;
} & Omit<
  AsyncAutocompleteWithQueryProps<AgreementResponse>,
  "fetchData" | "failMessage" | "labelField" | "idField" | "value" | "onChange"
>;

const SelectAgreement = ({
  value: agreementId,
  filter,
  onChange,
  parentOnChange,
  ...rest
}: SelectAgreementProps) => {
  const { formatMessage: t } = useIntl();
  const { agreementService } = useGlobalContext();
  const queryClient = useQueryClient();
  const [agreement, setAgreement] = useState<AgreementResponse | null>(null);
  const { organizationId } = contextStore.useState();

  const { isLoading, data } = useQuery(
    ["getAgreement", agreementId],
    () => agreementService.getAgreement(agreementId!),
    {
      enabled: !!agreementId && (!agreement || agreement.id !== agreementId),
    }
  );

  useEffect(() => {
    if (data?.data) {
      setAgreement(data?.data!);
    }
  }, [data, agreement]);

  const fetchData = async (searchTerm: string) => {
    const request = ListRequestOfAgreementFilter.fromJS({
      pageSize: 50,
      sortField: "name",
      parameters: AgreementFilter.fromJS({
        searchText: searchTerm === "" ? undefined : searchTerm,
        agreementStatusIds: filter?.agreementStatusIds,
      }),
    });
    const result = await queryClient.fetchQuery(
      ["agreementService.getAgreements", organizationId, request],
      () => agreementService.getAgreements(request)
    );

    return { result, forSearchTerm: searchTerm };
  };

  const onChangeInternal = (value: AgreementResponse | null) => {
    setAgreement(value);

    if (parentOnChange) parentOnChange(value);

    onChange(value?.id ?? null);
  };

  return (
    <AsyncAutocompleteWithQuery
      {...rest}
      value={agreement}
      isLoadingInitialValue={isLoading || rest.loading}
      labelField="name"
      idField="id"
      label={t({ id: "agreement" })}
      fetchData={fetchData}
      failMessage={t({ id: "fetchError" })}
      onChange={onChangeInternal}
    />
  );
};

export default SelectAgreement;
